const LANDING_PREFIX = '/landing';

/**
 * Is the given pathname a marketing landing-page path?
 * Tolerant of a trailing slash and query string.
 *
 * @arg {string} pathname - typically window.location.pathname
 * @return {boolean} true when the path is `/landing` or `/landing/...`
 */
export function isLandingPath(pathname) {
	if (typeof pathname !== 'string') return false;

	const path = pathname.split('?')[0];

	return path === LANDING_PREFIX || path.startsWith(`${LANDING_PREFIX}/`);
}

/**
 * Derive the campaign slug from a landing pathname.
 * `/landing/spring-promo` -> `spring-promo`; bare `/landing` -> `index`.
 * Non-landing paths return an empty string.
 *
 * @arg {string} pathname - typically window.location.pathname
 * @return {string} the campaign slug, `index` for a bare landing path, or `` when not a landing path
 */
export function getLandingSlug(pathname) {
	if (!isLandingPath(pathname)) return '';

	const path = pathname.split('?')[0].replace(/\/+$/, '');

	if (path === LANDING_PREFIX) return 'index';

	return path.slice(LANDING_PREFIX.length + 1) || 'index';
}

/**
 * Resolve the Rudderstack page category + name for the current path.
 * On a `/landing` path this returns the `Landing` category and the campaign
 * slug so the visit is tracked as a landing/campaign event instead of the
 * wildcard route's default; off landing paths it returns the given fallbacks.
 *
 * @arg {string} pathname - typically window.location.pathname
 * @arg {string} fallbackCategory - route meta category to use off landing paths
 * @arg {string} fallbackName - route meta name to use off landing paths
 * @return {{category: string, name: string}} the page category and name to fire
 */
export function landingPageMeta(pathname, fallbackCategory, fallbackName) {
	if (isLandingPath(pathname)) {
		return {
			category: 'Landing',
			name: getLandingSlug(pathname)
		};
	}

	return {
		category: fallbackCategory,
		name: fallbackName
	};
}
